import React, { createContext, useState, useContext, ReactNode, useEffect, useRef } from 'react';
import CryptoJS from 'crypto-js';
import { jwtDecode, JwtPayload } from 'jwt-decode';

const SECRET_KEY = CryptoJS.enc.Utf8.parse('12345678901234567890123456789012');
const backendUrl = process.env.REACT_APP_BACKEND_URL;

export const encryptData = (data: any) => {
  if (!data) {
    return '';
  }
  try {
    const dataStr = typeof data === 'string' ? data : JSON.stringify(data);
    const encrypted = CryptoJS.AES.encrypt(dataStr.trim(), SECRET_KEY, {
      mode: CryptoJS.mode.ECB,
      padding: CryptoJS.pad.Pkcs7,
    });
    return encrypted.toString();
  } catch (error) {
    return '';
  }
};

export const decryptData = (encryptedData: any) => {
  if (!encryptedData) {
    return null;
  }
  try {
    const decrypted = CryptoJS.AES.decrypt(encryptedData, SECRET_KEY, {
      mode: CryptoJS.mode.ECB,
      padding: CryptoJS.pad.Pkcs7,
    });
    return Number(decrypted.toString(CryptoJS.enc.Utf8));
  } catch (error) {
    return null;
  }
};

interface UserContextType {
  user_id: number | null;
  user_name: string | null;
  auth_token: string | null;
  city_name: string | null;
  city_pincode: string | null;
  utm_session_id: string | null;
  sess_code: string | null;
  globalSearchTerm: string;
  globalAddressErrors: {
    address_1: string;
    address_1_error_status: boolean;
    address_2: string;
    address_2_error_status: boolean;
    name: string;
    name_error_status: boolean;
    email: string;
    email_error_status: boolean;
    landmark: string;
    landmark_error_status: boolean;
  };
  globalUtmQueryRef: any;
  isAuthorized: boolean;
  previous_pincode:string | null;
  setUserId: (id: number | null) => void;
  setUsername: (name: string | null) => void;
  setAuthToken: (auth_token: string | null) => void;
  setcityName: (city_name: string | null) => void;
  setSessionCode: (sess_code: string) => void;
  setUtmSessionId: (utm_session_id: string | null) => void;
  checkSessionTokenExpirationStatus: () => boolean;
  handleRemoveLocalStorage: () => void;
  changeTitleAndMetaTags: (title: string, metaTags: { description?: string; keywords?: string; [key: string]: string | undefined }) => void;
  fetchCsrfToken: () => any;
  setGlobalSearchTerm: (globalSearchTerm: string) => void;
  handleAddressErrorChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  setPincode: (city_pincode: string | null) => void;
  setUserAuthorized: (status: boolean) => void;
  setPreviousPincode: (previous_pincode: string | null) => void;
}

// Create the context with default values
const UserContext = createContext<UserContextType | undefined>(undefined);

// Global context value store - must be declared after UserContextType interface
let contextValueStore: UserContextType | null = null;

// Store the setter function directly to avoid TypeScript control flow issues
let setUserAuthorizedRef: ((status: boolean) => void) | null = null;

// Export function to get context value from anywhere
export const getContextValue = (): UserContextType | null => contextValueStore;

// Export function to set isAuthorized from anywhere (like API files)
export const setIsAuthorizedDirectly = (status: boolean): void => {
  if (setUserAuthorizedRef) {
    setUserAuthorizedRef(status);
  }
};

const authorizedInit = ():boolean=>{
    const isAuthorized =  localStorage.getItem("isAuthorized");
   

    if(!isAuthorized || isAuthorized == 'false') 
      return false
    else return true

  }
   

export const UserProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user_id, setUserId] = useState<number | null>(
    decryptData(localStorage.getItem("user_id") || '') || null
  );
  const [user_name, setUsername] = useState<string | null>(
    localStorage.getItem("user_name")
  );
  const [auth_token, setAuthToken] = useState<string | null>(
    localStorage.getItem("auth_token")
  );
  const [city_name, setcityName] = useState<string | null>(
    localStorage.getItem("city_name")
  );
  const [city_pincode, setPincode] = useState<string | null>(
    localStorage.getItem("city_pincode")
  );
  const [isAuthorized, setUserAuthorized] = useState<boolean>( authorizedInit());
    const [previous_pincode, setPreviousPincode] = useState<string | null>(
    localStorage.getItem("previous_pincode")
  );

  const [sess_code, setSessionCode] = useState<string | null>(
    localStorage.getItem("sess_code")
  );
  const [utm_session_id, setUtmSessionId] = useState<string | null>(
    localStorage.getItem("utm_session_id")
  );
  const [globalSearchTerm, setGlobalSearchTerm] = useState<string>("");
  const [globalAddressErrors, setGlobalAddressErrors] = useState({
    address_1: "",
    address_1_error_status: false,
    address_2: "",
    address_2_error_status: false,
    name: "",
    name_error_status: false,
    email: "",
    email_error_status: false,
    landmark: "",
    landmark_error_status: false,
  });
  let globalUtmQueryRef = useRef<any>("");

  useEffect(() => {
    if (user_id) {
      localStorage.setItem("user_id", encryptData(user_id));
    } else {
      localStorage.removeItem("user_id");
    }
  }, [user_id]);

  useEffect(() => {
    if (user_name) {
      localStorage.setItem("user_name", user_name);
    } else {
      localStorage.removeItem("user_name");
    }
  }, [user_name]);

  useEffect(() => {
   
 
      
      localStorage.setItem("isAuthorized", String(isAuthorized));
    
  }, [isAuthorized]);

  useEffect(() => {
    if (auth_token) {
      localStorage.setItem("auth_token", auth_token);
      localStorage.setItem(
        "auth_token_timestamp",
        new Date().getTime().toString()
      );
    } else {
      // localStorage.removeItem("auth_token");
      // localStorage.removeItem("auth_token_timestamp");
    }
  }, [auth_token]);

  useEffect(() => {
    if (city_name) {
      localStorage.setItem("city_name", city_name);
    } else {
      localStorage.removeItem("city_name");
    }
  }, [city_name]);

  useEffect(() => {
    if (city_pincode) {
      localStorage.setItem("city_pincode", city_pincode);
    } else {
      localStorage.removeItem("city_pincode");
    }
  }, [city_pincode]);

     useEffect(() => {
    if (previous_pincode) {
      localStorage.setItem("previous_pincode", previous_pincode);
    } else {
      localStorage.removeItem("previous_pincode");
    }
  }, [previous_pincode]);

  useEffect(() => {
    if (sess_code) {
      localStorage.setItem("sess_code", sess_code);
    } else {
      localStorage.removeItem("sess_code");
    }
  }, [sess_code]);

  useEffect(() => {
    if (utm_session_id) {
      localStorage.setItem("utm_session_id", utm_session_id);
    } else {
      localStorage.removeItem("utm_session_id");
    }
  }, [utm_session_id]);

  const handleRemoveLocalStorage = () => {
   
    // sessionStorage.clear();
    localStorage.removeItem("isLoggedIn");
    localStorage.removeItem("user_id");
    localStorage.removeItem("userName");
    // localStorage.removeItem("auth_token");
    localStorage.removeItem("city_name");
    localStorage.removeItem("city_pincode");
    localStorage.removeItem("cartQuantity");
    localStorage.removeItem("sess_code");
    sessionStorage.removeItem("session_code");
    sessionStorage.removeItem("user_id");
    sessionStorage.removeItem("cartQuantity");
       localStorage.removeItem("previous_pincode");
      sessionStorage.clear()
        localStorage.clear()
  };

  const changeTitleAndMetaTags = async (
    title: string,
    metaTags: { description?: string; keywords?: string; [key: string]: string | undefined }
  ) => {
    try {

      console.log("blogs pagesssss",metaTags)
      if (title) {
        document.title = title;
      } else {
        document.title = "Sell Used Appliances Online in India - Instant Cash & Free Pickup | Selsmart";
      }

      Object.keys(metaTags).forEach((metaName) => {
        if (metaName === 'cronical_url') return;
        const metaTag = document.querySelector(`meta[name="${metaName}"]`);
        if (metaTag) {
          metaTag.setAttribute('content', metaTags[metaName] || `Default ${metaName} for Selsmart`);
        } else {
          const newMetaTag = document.createElement('meta');
          newMetaTag.name = metaName;
          newMetaTag.content = metaTags[metaName] || `Default ${metaName} for Selsmart`;
          document.head.appendChild(newMetaTag);
        }
      });

      if (metaTags.cronical_url) {
        let canonicalLink = document.querySelector('link[rel="canonical"]');
        if (canonicalLink) {
          canonicalLink.setAttribute('href', metaTags.cronical_url);
        } else {
          canonicalLink = document.createElement('link');
          canonicalLink.setAttribute('rel', 'canonical');
          canonicalLink.setAttribute('href', metaTags.cronical_url);
          document.head.appendChild(canonicalLink);
        }
      }

      return () => {
        document.title = "Sell Used Appliances Online in India - Instant Cash & Free Pickup | Selsmart";
        Object.keys(metaTags).forEach((metaName) => {
          if (metaName === 'cronical_url') return;
          const metaTag = document.querySelector(`meta[name="${metaName}"]`);
          if (metaTag) {
            metaTag.setAttribute('content', `Default ${metaName} for Selsmart`);
          }
        });

        const canonicalLink = document.querySelector('link[rel="canonical"]');
        if (canonicalLink) {
          canonicalLink.setAttribute('href', window.location.href);
        }
      };
    } catch (error) {
      // Error handling
    }
  };

  const fetchCsrfToken = async () => {
    const response = await fetch(`${backendUrl}api/v1/csrf-token`, {
      method: 'GET',
      headers: {
        "Content-Type": "application/json",
      },
      credentials: "include",
      mode: "cors"
    });
    const data = await response.json();
    return data.csrfToken;
  };

  const getTokenExpirationTime = (token: string): number | null => {
    try {
      const decodedToken = jwtDecode<JwtPayload & { exp?: number }>(token);
      if (decodedToken.exp) {
        return decodedToken.exp * 1000;
      }
      return null;
    } catch (error) {
      return null;
    }
  };

  const checkSessionTokenExpirationStatus = () => {
    let auth_token = localStorage.getItem("auth_token") as string;
    let auth_token_exp = getTokenExpirationTime(auth_token);
    if (auth_token_exp && auth_token_exp <= new Date().getTime()) {
      return true;
    } else {
      return false;
    }
  };

  const handleAddressErrorChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let updatedValue = value.trimStart();

    let lengthError = "Please enter at least 2 characters";
    switch (name) {
      case "address_1":
        if (updatedValue.length === 0) {
          setGlobalAddressErrors((prev) => ({
            ...prev,
            address_1: "",
            address_1_error_status: false,
          }));
        } else if (updatedValue.length < 2) {
          setGlobalAddressErrors((prev) => ({
            ...prev,
            address_1: lengthError,
            address_1_error_status: true,
          }));
        } else {
          setGlobalAddressErrors((prev) => ({
            ...prev,
            address_1: "",
            address_1_error_status: false,
          }));
        }
        break;

      case "address_2":
        if (updatedValue.length === 0) {
          setGlobalAddressErrors((prev) => ({
            ...prev,
            address_2: "",
            address_2_error_status: false,
          }));
        } else if (updatedValue.length < 2) {
          setGlobalAddressErrors((prev) => ({
            ...prev,
            address_2: lengthError,
            address_2_error_status: true,
          }));
        } else {
          setGlobalAddressErrors((prev) => ({
            ...prev,
            address_2: "",
            address_2_error_status: false,
          }));
        }
        break;

      case "name":
        if (updatedValue.length === 0) {
          setGlobalAddressErrors((prev) => ({
            ...prev,
            name: "",
            name_error_status: false,
          }));
        } else if (updatedValue.length < 2) {
          setGlobalAddressErrors((prev) => ({
            ...prev,
            name: lengthError,
            name_error_status: true,
          }));
        } else {
          setGlobalAddressErrors((prev) => ({
            ...prev,
            name: "",
            name_error_status: false,
          }));
        }
        break;

      case "email":
        const trimmedEmail = updatedValue.replace(/\s/g, '');

        if (trimmedEmail.length === 0) {
          setGlobalAddressErrors((prev) => ({
            ...prev,
            email: "Please enter a valid email id",
            email_error_status: true,
          }));
        } else if (!/^[a-zA-Z0-9@._-]+$/.test(trimmedEmail)) {
          setGlobalAddressErrors((prev) => ({
            ...prev,
            email: "Only alphabets, numbers, and special characters (@, ., -, _) are allowed",
            email_error_status: true,
          }));
        } else if (!/^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/.test(trimmedEmail)) {
          setGlobalAddressErrors((prev) => ({
            ...prev,
            email: "Please enter a valid email id",
            email_error_status: true,
          }));
        } else if (/^[0-9]+@/.test(trimmedEmail)) {
          setGlobalAddressErrors((prev) => ({
            ...prev,
            email: "The part before @ cannot be only numeric",
            email_error_status: true,
          }));
        } else if (trimmedEmail.length < 10) {
          setGlobalAddressErrors((prev) => ({
            ...prev,
            email: "Please enter a valid email id",
            email_error_status: true,
          }));
        } else {
          setGlobalAddressErrors((prev) => ({
            ...prev,
            email: "",
            email_error_status: false,
          }));
        }
        break;

      case "landmark":
        if (updatedValue.length === 0) {
          setGlobalAddressErrors((prev) => ({
            ...prev,
            landmark: "",
            landmark_error_status: false,
          }));
        } else if (updatedValue.length < 2) {
          setGlobalAddressErrors((prev) => ({
            ...prev,
            landmark: lengthError,
            landmark_error_status: true,
          }));
        } else {
          setGlobalAddressErrors((prev) => ({
            ...prev,
            landmark: "",
            landmark_error_status: false,
          }));
        }

      default:
        break;
    }
  };

  useEffect(() => {
    const handlePopState = (event: any) => {
       
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, []);

  // Create the context value object
  const contextValue: UserContextType = {
    user_id,
    user_name,
    auth_token,
    city_name,
    utm_session_id,
    sess_code,
    globalSearchTerm,
    globalAddressErrors,
    globalUtmQueryRef,
    city_pincode,
    isAuthorized,
    previous_pincode,
    setUserId,
    setUsername,
    setAuthToken,
    setcityName,
    setUtmSessionId,
    setSessionCode,
    checkSessionTokenExpirationStatus,
    handleRemoveLocalStorage,
    fetchCsrfToken,
    setGlobalSearchTerm,
    handleAddressErrorChange,
    changeTitleAndMetaTags,
    setPincode,
    setUserAuthorized,
    setPreviousPincode
  };

  // Store it globally so API files can access it
  contextValueStore = contextValue;
  
  // Also store the setter function directly
  setUserAuthorizedRef = setUserAuthorized;

  return (
    <UserContext.Provider value={contextValue}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => {
  const context = useContext(UserContext);
  if (context === undefined) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
};